/**
 * Core Workflow Tutorial (Movie Summarizer) - Beginner
 *
 * 18 steps: Project -> Agent -> Workflow -> Run
 */

import { TutorialStep } from '../../types/tutorial';

/**
 * Specific values for the core workflow tutorial.
 */
export const TUTORIAL_VALUES = {
  project: {
    name: 'Tutorials',
    description:
      'A collection of tutorial projects to learn Agent Commander features and best practices',
  },
  agent: {
    name: 'Movie Summarizer',
    instructions: `You are a witty entertainment critic who creates hilarious, bite-sized summaries.

When given a movie or book title:
1. Write a humorous summary in 100 words or less
2. Capture the essence of the story with comedic flair
3. Include at least one clever observation or joke
4. Keep it family-friendly but genuinely funny

Your tone should be playful and entertaining, like you're describing the plot to a friend at a party. Feel free to poke fun at cliches, plot holes, or character quirks.

Example input: "Titanic"
Example output: "Rich girl meets poor artist on fancy boat. They fall in love over a flying pose at the front of the ship. Iceberg disagrees with their romance. Turns out the door WAS big enough for two, but we don't talk about that. She promises to never let go, then immediately lets go. 84 years later, she still has the expensive necklace and tosses it into the ocean for dramatic effect. Jack deserved better."`,
  },
  orchestration: {
    name: 'Fun Summaries',
    description:
      'This workflow creates humorous content summaries based on user input of a movie.',
  },
  userPrompt: {
    label: 'Get Movie Title',
    inputMessage: "Enter a movie title and I'll create a hilarious summary!",
  },
  agentStep: {
    label: 'Generate Summary',
  },
};

/**
 * The tutorial steps for the core workflow tutorial.
 */
export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    stepNumber: 1,
    targetSelector: '[data-tutorial="nav-projects"]',
    title: 'Welcome to Agent Commander!',
    description:
      "Let's build something fun together! We're going to create an AI agent that writes " +
      'hilarious movie summaries. First, we need a project to organize our work. ' +
      'Think of projects as folders that keep your AI agents and workflows organized.',
    actionPrompt: "Click 'Projects' in the sidebar to get started.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-projects"]',
    },
  },
  {
    stepNumber: 2,
    targetSelector: '[data-tutorial="btn-create-project"]',
    title: 'Create Your First Project',
    description:
      'Great! Now let\'s create a project called "Tutorials" where our Movie Summarizer ' +
      'agent will live. Projects help you keep different AI applications separate and organized.',
    actionPrompt: "Click the 'New Project' button to create your project.",
    position: 'bottom',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-project"]',
    },
    expectedRoute: '/projects',
  },
  {
    stepNumber: 3,
    targetSelector: '[data-tutorial="form-project"]',
    title: 'Name Your Project',
    description:
      "Let's set up your project with these details:\n\n" +
      '__Tip:__ Click the copy icon next to highlighted values to copy them!\n\n' +
      '1. For Project Name, enter: **Tutorials**\n\n' +
      '2. For Description, enter: **A collection of tutorial projects to learn Agent Commander features and best practices**\n\n' +
      'This project will hold all your tutorial agents and workflows.',
    actionPrompt:
      "Enter the project details above, then click 'Create Project' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-project"]',
    },
    resumeFallbackStep: 2,
  },
  {
    stepNumber: 4,
    targetSelector: '[data-tutorial="project-info-card"]',
    title: 'Your Project Overview',
    description:
      'Welcome to your project overview! This is your command center for managing AI workflows.\n\n' +
      "__What you'll find here:__\n\n" +
      '• __Project Details__ - Name, description, and settings\n\n' +
      '• __Agents__ - AI assistants you create for this project\n\n' +
      '• __Workflows__ - Connect agents together\n\n' +
      '• __Run History__ - Track past executions and results\n\n' +
      'Each project keeps everything organized and separate from other projects.',
    actionPrompt:
      "Click 'Continue' when you're ready to create your first agent!",
    position: 'right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/projects/:id',
  },
  {
    stepNumber: 5,
    targetSelector: '[data-tutorial="nav-agents"]',
    title: 'Time to Create Your Agent!',
    description:
      'Excellent! Your project is ready. Now comes the fun part - creating your AI agent! ' +
      'Agents are AI-powered assistants that follow your instructions. Our agent will be a ' +
      'witty entertainment critic who writes funny movie summaries.',
    actionPrompt: "Click 'Agents' in the sidebar to continue.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-agents"]',
    },
  },
  {
    stepNumber: 6,
    targetSelector: '[data-tutorial="btn-create-agent"]',
    title: 'Create Your Movie Summarizer',
    description:
      "Now we'll create the Movie Summarizer agent. When you click the button, " +
      "you'll see a dropdown menu with two options. Select __Create from Scratch__ to " +
      'build a custom agent with your own personality and instructions.',
    actionPrompt:
      "Click 'New Agent', then select __Create from Scratch__ from the dropdown.",
    position: 'left',
    tooltipMaxWidth: 260,
    tooltipOffsetX: -120,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-from-scratch"]',
    },
  },
  {
    stepNumber: 7,
    targetSelector: '[data-tutorial="form-agent"]',
    title: 'Configure Your Agent',
    description:
      "Let's bring your Movie Summarizer to life! Fill in these fields:\n\n" +
      '1. Agent Name: **Movie Summarizer**\n\n' +
      "2. Instructions: This is where you define your agent's personality. Copy and paste:\n" +
      '**You are a witty entertainment critic who creates hilarious, bite-sized summaries. ' +
      'When given a movie or book title, write a humorous summary in 100 words or less. ' +
      'Capture the essence with comedic flair and include at least one clever joke.**\n\n' +
      'Leave the other settings at their defaults.',
    actionPrompt:
      "Fill in the Agent Name and Instructions, then click 'Create Agent'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-agent"]',
    },
    resumeFallbackStep: 6,
  },
  {
    stepNumber: 8,
    targetSelector: '[data-tutorial="nav-workflows"]',
    title: 'Build Your Workflow',
    description:
      "Your Movie Summarizer agent is ready! Now let's create a workflow - " +
      'a workflow that connects user input to your agent. Workflows let you ' +
      'build powerful AI pipelines.',
    actionPrompt: "Click 'Workflows' in the sidebar to continue.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-workflows"]',
    },
  },
  {
    stepNumber: 9,
    targetSelector: '[data-tutorial="btn-create-workflow"]',
    title: 'Create Your Workflow',
    description:
      'Time to set up your first workflow! This will connect user input ' +
      'to your Movie Summarizer agent.',
    actionPrompt: "Click 'New Workflow' to create your workflow.",
    position: 'bottom',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-workflow"]',
    },
  },
  {
    stepNumber: 10,
    targetSelector: '[data-tutorial="form-workflow"]',
    title: 'Name Your Workflow',
    description:
      "Let's set up your workflow:\n\n" +
      '1. For Name, enter: **Fun Summaries**\n\n' +
      '2. For Description, enter: **This workflow creates humorous content summaries based on user input of a movie.**',
    actionPrompt: "Enter the details and click 'Create' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-workflow"]',
    },
    resumeFallbackStep: 9,
  },
  {
    stepNumber: 11,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add Your First Step',
    description:
      "Welcome to the workflow editor! You'll see a __Start__ node with a __+__ button below it.\n\n" +
      'The __+__ button lets you add steps to your workflow. First, we need a way for users to ' +
      "provide input - we'll add a __User Prompt__ step.",
    actionPrompt: 'Click the __+__ button below the Start node to add a step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage:
      'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
      'Navigate to __Workflows__ in the sidebar and click on the "Fun Summaries" workflow you created.',
  },
  {
    stepNumber: 12,
    targetSelector: '[data-tutorial="step-type-user_prompt"]',
    title: 'Select User Prompt',
    description:
      "A step selection panel has appeared. You'll see different step types you can add.\n\n" +
      'The __User Prompt__ step lets users enter text that gets passed to your agent. ' +
      'This is perfect for getting a movie title from the user!',
    actionPrompt: 'Click on __User Prompt__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-user_prompt"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage:
      'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
      'Navigate to __Workflows__ in the sidebar and click on the "Fun Summaries" workflow.',
    resumeFallbackStep: 11,
  },
  {
    stepNumber: 13,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure User Prompt',
    description:
      "Now let's configure the User Prompt step. Fill in these fields:\n\n" +
      '__Label__: **Get Movie Title**\n' +
      'This identifies the step in your workflow.\n\n' +
      "__Input Message__: **Enter a movie title and I'll create a hilarious summary!**\n" +
      'This message is shown to users when they run the workflow.',
    actionPrompt: "Fill in the Label and Input Message, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage:
      'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
      'Navigate to **Workflows** in the sidebar and click on the "Fun Summaries" workflow.',
    resumeFallbackStep: 11,
  },
  {
    stepNumber: 14,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add Your Agent Step',
    description:
      "Excellent! Your User Prompt step is now in the workflow. You'll see a new __+__ button " +
      'below it.\n\n' +
      "Now we need to add your __Movie Summarizer__ agent to process the user's input.",
    actionPrompt: 'Click the __+__ button below your User Prompt step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage:
      'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
      'Navigate to __Workflows__ in the sidebar and click on the "Fun Summaries" workflow.',
  },
  {
    stepNumber: 15,
    targetSelector: '[data-tutorial="step-option-run_agent-create"]',
    title: 'Select Agent - Create',
    description:
      "The Step Type picker has appeared with several options. We want our Movie Summarizer " +
      'to **generate** a new summary from the user\'s input, so we\'ll use the __Create__ intent.\n\n' +
      'The other options - __Assistant__ (for interactive back-and-forth) and __Conditional__ ' +
      "(for branching logic) - are for more advanced workflows you'll see in later tutorials.",
    actionPrompt: 'Click on __Agent - Create__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-create"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage:
      'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
      'Navigate to __Workflows__ in the sidebar and click on the "Fun Summaries" workflow.',
    resumeFallbackStep: 14,
  },
  {
    stepNumber: 16,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure Agent Step',
    description:
      "Now let's configure the Agent step:\n\n" +
      '__Select Agent__: Choose __Movie Summarizer__ from the dropdown.\n' +
      'This is the agent you created earlier!\n\n' +
      'If the dropdown is empty, click the __+__ button next to it to create a new agent.\n\n' +
      "The __Label__ will be automatically set to the agent's name.\n\n" +
      "Leave the other settings at their defaults - they're already configured correctly.",
    actionPrompt:
      "Select Movie Summarizer from the dropdown, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage:
      'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
      'Navigate to __Workflows__ in the sidebar and click on the "Fun Summaries" workflow.',
    resumeFallbackStep: 14,
  },
  {
    stepNumber: 17,
    targetSelector: '[data-tutorial="btn-run-workflow"]',
    title: 'Run Your First Workflow!',
    description:
      'Your workflow is complete! When you click Execute:\n\n' +
      "• You'll be prompted to enter a movie title\n" +
      '• Try **The Matrix** or **Finding Nemo**\n' +
      '• Your Movie Summarizer will create a hilarious summary!',
    actionPrompt: "Click 'Execute' to run your workflow!",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-run-workflow"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage:
      'To finish the tutorial, you need to be in the Workflow Editor.\n\n' +
      'Navigate to __Workflows__ in the sidebar and click on the "Fun Summaries" workflow to run it.',
  },
  {
    stepNumber: 18,
    targetSelector: '[data-tutorial="btn-submit-user-input"]',
    title: 'Submit Your Input',
    description:
      'The workflow is waiting for your input! Type a movie title in the text field and click **Submit**.\n\n' +
      'Try **The Matrix**, **Finding Nemo**, or any movie you like!',
    actionPrompt: "Enter a movie title and click 'Submit'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-user-input"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage:
      'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
      'Navigate to __Workflows__ in the sidebar and click on the "Fun Summaries" workflow.',
  },
  {
    stepNumber: 19,
    targetSelector: '[data-tutorial="btn-view-final-output"]',
    title: 'View Your Results!',
    description:
      'Your workflow is running! Once it completes, you can click __View Final Output__ to see ' +
      "your Movie Summarizer's creation.\n\n" +
      '__Tip:__ Want a starting point for your next workflow? On the Workflows page, hover any row and ' +
      'click the __Duplicate__ icon (between Edit and Delete) to copy this workflow - into the same project, ' +
      'or into another one with its agent auto-cloned for you.\n\n' +
      "When you're ready, click __Finish Tutorial__ below to complete the tutorial!",
    actionPrompt:
      "Wait for the workflow to complete, then click 'View Final Output'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage:
      'To finish the tutorial, you need to be in the Workflow Editor.\n\n' +
      'Navigate to __Workflows__ in the sidebar, click on the "Fun Summaries" workflow, ' +
      'and run it to see the View Final Output button.',
  },
];
